import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Paths } from "../constants/Paths";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Clock, Check, Plus, Minus, Info } from 'lucide-react';
import { ChevronDownIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Custom DatePicker component adapted from user request
function DatePicker({ value, onChange, placeholder = "Select date" }) {
    const [open, setOpen] = React.useState(false);

    // Parse string YYYY-MM-DD to Date object or undefined
    const dateValue = value ? new Date(value) : undefined;

    const handleSelect = (newDate) => {
        setOpen(false);
        if (newDate) {
            // Format to YYYY-MM-DD for consistency with standard date input
            // Using local time to avoid timezone issues with pure dates if possible, 
            // but simplest is to use year-month-day from the date object.
            // NOTE: date-fns format 'yyyy-MM-dd' is reliable.
            onChange(format(newDate, 'yyyy-MM-dd'));
        } else {
            onChange("");
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn(
                        "w-full justify-between font-normal mt-1.5",
                        !dateValue && "text-muted-foreground"
                    )}
                >
                    {dateValue ? format(dateValue, "PPP") : placeholder}
                    <ChevronDownIcon className="h-4 w-4 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={dateValue}
                    onSelect={handleSelect}
                    disabled={(date) =>
                        date > new Date() || date < new Date("1900-01-01")
                    }
                    initialFocus
                    captionLayout="dropdown" // Using dropdown as requested in "Calendar22" snippet indirectly
                    fromYear={1900}
                    toYear={new Date().getFullYear()}
                />
            </PopoverContent>
        </Popover>
    );
}

import { useDispatch } from 'react-redux';
import { getSearchFilters } from "@/src/actions/flightAction";
import { getTourPackageDetail, getTourAvailPrice, bookTourPackage } from "@/src/actions/tour/tourAction";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from '../provider/AuthProvider';
import useSEO from '../hooks/useSEO';

export default function TourBooking() {
    const { packageId: urlPackageId, pricingId: urlPricingId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { token } = useAuth();
    const { setSEO } = useSEO();

    // Booking data state
    const [bookingData, setBookingData] = useState(location.state || {});
    const [loading, setLoading] = useState(!location.state);
    const [error, setError] = useState(null);

    React.useEffect(() => {
        if (bookingData?.packageTitle) {
            setSEO(`Book ${bookingData.packageTitle} | Yatra Tickets`);
        } else {
            setSEO(`Tour Booking | Yatra Tickets`);
        }
    }, [bookingData, setSEO]);

    const viewTourDetail = (tourBookId) => {
        let bookUID = null;
        let tourImage = image;
        let id = tourBookId;

        if (tourBookId && typeof tourBookId === 'object') {
            bookUID = tourBookId.BookUID;

            if (tourBookId.tourPackFiles && tourBookId.tourPackFiles.length > 0) {
                tourImage = tourBookId.tourPackFiles[0].FileURL;
            }
            id = tourBookId.TourBookID || tourBookId.id || tourBookId.ID;
        }

        if (id) {
            navigate(Paths.TOUR_PAYMENT, {
                state: {
                    tourBookId: id,
                    bookUID: bookUID,
                    packageTitle,
                    totalFare: (pricePerPerson || 0) * travelerCount,
                    travelerCount,
                    pricePerPerson,
                    image: tourImage,
                    startplace,
                    endplace,
                    departureDate,
                    returnDate,
                    tourType,
                }
            });
        }
    };

    React.useEffect(() => {
        if (!token) {
            navigate(Paths.HOME, { replace: true });
            toast({
                variant: "destructive",
                title: "Authentication Required",
                description: "You must be logged in to book a tour.",
            });
        }
    }, [token, navigate]);

    const {
        packageTitle,
        departureDate,
        returnDate,
        departureDateRaw,
        pricePerPerson,
        tourId,
        pricingId,
        tourType,
        image,
        startplace,
        endplace,
    } = bookingData;

    const [travelerCount, setTravelerCount] = useState(1);
    const [searchFilters, setSearchFilters] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const createTraveler = (id, type) => ({
        id,
        type,
        title: "",
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        nationality: "",
        email: "",
        //confirmEmail: "",
        mobileNumber: "",
        countryCode: "np",
    });

    const [travelers, setTravelers] = useState([createTraveler(1, "Lead Traveler")]);
    const [bookingStep, setBookingStep] = useState('form'); // 'form' | 'review'

    React.useEffect(() => {
        dispatch(getSearchFilters((filters) => {
            if (filters) {
                setSearchFilters(filters);
            }
        }));

        if (!location.state && urlPackageId && urlPricingId) {
            setLoading(true);
            setError(null);

            getTourPackageDetail(urlPackageId, (pkgData) => {
                if (!pkgData) {
                    setError("Tour package not found.");
                    setLoading(false);
                    return;
                }

                getTourAvailPrice(urlPackageId, (availData) => {
                    setLoading(false);
                    if (!availData) {
                        setError("Pricing information not available.");
                        return;
                    }

                    const options = Array.isArray(availData) ? availData : [availData];
                    const selectedPricing = options.find(opt =>
                        String(opt.TourPricingID) === String(urlPricingId)
                    );

                    if (!selectedPricing) {
                        setError("Specific pricing option not found.");
                        return;
                    }

                    const startOriginal = selectedPricing.DepartureDate;
                    const durationNum = pkgData.Package?.DurationDays || 0;

                    const formatDate = (dateStr) => {
                        if (!dateStr) return "";
                        const d = new Date(dateStr);
                        if (isNaN(d.getTime())) return dateStr;
                        return d.toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                        });
                    };

                    const addDays = (dateStr, days) => {
                        if (!dateStr || !days) return dateStr;
                        const d = new Date(dateStr);
                        if (isNaN(d.getTime())) return dateStr;
                        d.setDate(d.getDate() + parseInt(days, 10));
                        return d.toISOString();
                    };

                    const endOriginal = durationNum > 0 ? addDays(startOriginal, durationNum - 1) : startOriginal;

                    const itineraries = pkgData.Itineraries || [];
                    const startplace = itineraries.length > 0 ? itineraries[0].Destination : "";
                    const endplace = itineraries.length > 0 ? itineraries[itineraries.length - 1].Destination : "";

                    setBookingData({
                        packageTitle: pkgData.Package?.Title,
                        departureDate: formatDate(startOriginal),
                        returnDate: formatDate(endOriginal),
                        departureDateRaw: startOriginal,
                        pricePerPerson: (selectedPricing.TourFare || 0) + (selectedPricing.FlightFare || 0),
                        tourId: urlPackageId,
                        pricingId: urlPricingId,
                        tourType: selectedPricing.IsGroup ? "Group Tour" : "Fixed Departure",
                        image: pkgData.Destination?.ImageUrl,
                        startplace,
                        endplace
                    });
                });
            });
        }
    }, [dispatch, location.state, urlPackageId, urlPricingId]);

    const handleTravelerCountChange = (increment) => {
        setTravelerCount(prev => {
            const newVal = prev + increment;
            if (newVal < 1) return 1;

            if (increment > 0) {
                setTravelers(prevTravelers => [
                    ...prevTravelers,
                    createTraveler(newVal, `Traveler ${newVal}`)
                ]);
            } else {
                setTravelers(prevTravelers => prevTravelers.slice(0, newVal));
            }
            return newVal;
        });
    };

    const handleInputChange = (index, field, value) => {
        setTravelers(prev => {
            const newTravelers = [...prev];
            newTravelers[index] = { ...newTravelers[index], [field]: value };
            return newTravelers;
        });
    };

    const handleReview = () => {
        const lead = travelers[0];
        if (!lead.email || !lead.mobileNumber || !lead.firstName || !lead.lastName) {
            toast({
                variant: "destructive",
                title: "Missing Information",
                description: "Please fill in all required fields for the Lead Traveler."
            });
            return;
        }
        setBookingStep('review');
        window.scrollTo(0, 0); // Scroll to top for review
    };

    const handleSubmit = () => {
        if (isSubmitting) return;

        // if (lead.email !== lead.confirmEmail) {
        //     toast({
        //         variant: "destructive",
        //         title: "Email Mismatch",
        //         description: "Email and Confirm Email do not match."
        //     });
        //     return;
        // }

        let finalDepartureDate = departureDateRaw;
        if (!finalDepartureDate && departureDate) {
            const d = new Date(departureDate);
            if (!isNaN(d.getTime())) {
                finalDepartureDate = format(d, 'yyyy-MM-dd');
            } else {
                finalDepartureDate = departureDate;
            }
        }
        if (finalDepartureDate && finalDepartureDate.includes('T')) {
            finalDepartureDate = finalDepartureDate.split('T')[0];
        }

        const payload = {
            PackageID: tourId,
            TourPricingID: pricingId,
            TotalBookedSeat: travelerCount,
            DepartureDate: finalDepartureDate,
            Customers: travelers.map((t, idx) => ({
                CustomerCode: null,
                Title: t.title ? t.title.charAt(0).toUpperCase() + t.title.slice(1) : t.title,
                PaxType: "Adult",
                FirstName: t.firstName,
                MiddleName: "",
                LastName: t.lastName,
                DateOfBirth: t.dateOfBirth,
                Nationality: t.nationality,
                Email: t.email || null,
                MobileNumber: t.mobileNumber || "",
                IsContactPerson: idx === 0
            }))
        };

        //console.log("Booking Payload:", payload);
        setIsSubmitting(true);
        dispatch(bookTourPackage(payload, (resp) => {
            setIsSubmitting(false);
            if (resp && resp.IsSuccess) {
                toast({
                    title: "Booking Successful",
                    description: "Your tour has been booked successfully!",
                });
                // setTimeout(() => {
                //     navigate("/");
                // }, 1000);
                // console.log("Booking Response:", resp);
                setTimeout(() => {
                    //sentEmail(resp?.Result);
                    viewTourDetail(resp?.Result);
                }, 1000);
            }
            else {
                toast({
                    variant: "destructive",
                    title: "Booking Failed",
                    description: resp?.Message || "Your tour booking failed. Please try again.",
                });
            }
        }));
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4 lg:p-8 text-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="text-muted-foreground">Loading booking details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4 lg:p-8 text-center space-y-4">
                <div className="bg-destructive/10 text-destructive p-4 rounded-lg inline-block">
                    <p className="font-bold">Error</p>
                    <p>{error}</p>
                </div>
                <div className="pt-4">
                    <Button onClick={() => navigate(-1)}>Go Back</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 lg:p-8 space-y-8 max-w-7xl">
            {/* Timer Alert */}
            {/* <div className="bg-green-100 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                    <Clock className="w-4 h-4" />
                    Your departure date and seats are reserved for the next <span className="font-bold">10 minutes</span>
                </div>
                <button className="text-green-800 hover:text-green-900">
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div> */}

            <div className="grid lg:grid-cols-3 gap-8">
                {/* Left Column - Forms */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Header Tabs (Visual Only for now) */}
                    {/* <div className="flex border-b">
                        <div className="px-6 py-3 border-b-2 border-green-600 font-bold text-green-800">Your Details</div>
                        <div className="px-6 py-3 text-muted-foreground font-medium">Payment</div>
                    </div> */}

                    {/* Travelers Count */}
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold">Travelers</h2>
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                            <span className="font-medium">Number Of Travelers</span>
                            <div className="flex items-center gap-4">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8 rounded-full"
                                    onClick={() => handleTravelerCountChange(-1)}
                                    disabled={travelerCount <= 1}
                                >
                                    <Minus className="h-4 w-4" />
                                </Button>
                                <span className="w-4 text-center font-bold">{travelerCount}</span>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8 rounded-full"
                                    onClick={() => handleTravelerCountChange(1)}
                                >
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Traveler Details Form or Review */}
                    {bookingStep === 'form' ? (
                        /* Traveler Details Form */
                        <>
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <h2 className="text-xl font-bold">Traveler Details</h2>
                                    <p className="text-sm text-muted-foreground">Traveler information should match passport details. Lead traveler will serve as the contact person for the booking.</p>
                                </div>

                                {travelers.map((traveler, index) => (
                                    <Card key={traveler.id} className="border shadow-sm">
                                        <CardHeader className="pb-4 bg-gray-50/50 border-b">
                                            <div className="flex items-center gap-2">
                                                <div className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                                                    {index + 1}
                                                </div>
                                                <span className="font-bold">Traveler {index + 1}</span>
                                                {index === 0 && <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded ml-2">Lead Traveler</span>}
                                            </div>
                                        </CardHeader>
                                        <CardContent className="p-6 grid gap-6">
                                            {/* Form Content */}
                                            {index === 0 ? (
                                                // Lead Traveler Form (Existing Detailed)
                                                <>
                                                    {/* Name Fields */}
                                                    <div className="grid md:grid-cols-6 gap-4">
                                                        <div className="md:col-span-1">
                                                            <Label htmlFor={`title-${index}`} className="text-xs font-bold text-gray-500 uppercase">Title</Label>
                                                            <Select
                                                                value={traveler.title}
                                                                onValueChange={(val) => handleInputChange(index, 'title', val)}
                                                            >
                                                                <SelectTrigger id={`title-${index}`} className="mt-1.5"><SelectValue placeholder="Title" /></SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="mr">Mr</SelectItem>
                                                                    <SelectItem value="mrs">Mrs</SelectItem>
                                                                    <SelectItem value="ms">Ms</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div className="md:col-span-2">
                                                            <Label htmlFor={`fname-${index}`} className="text-xs font-bold text-gray-500 uppercase">First Name</Label>
                                                            <Input
                                                                id={`fname-${index}`}
                                                                placeholder="First Name"
                                                                className="mt-1.5"
                                                                value={traveler.firstName}
                                                                onChange={(e) => handleInputChange(index, 'firstName', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="md:col-span-3">
                                                            <Label htmlFor={`lname-${index}`} className="text-xs font-bold text-gray-500 uppercase">Last Name</Label>
                                                            <Input
                                                                id={`lname-${index}`}
                                                                placeholder="Last Name"
                                                                className="mt-1.5"
                                                                value={traveler.lastName}
                                                                onChange={(e) => handleInputChange(index, 'lastName', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Contact Info (Only for Lead) */}
                                                    {index === 0 && (
                                                        <>
                                                            <div className="grid md:grid-cols-2 gap-4">
                                                                <div>
                                                                    <Label htmlFor="email" className="text-xs font-bold text-gray-500 uppercase">Email Address (Required)</Label>
                                                                    <Input
                                                                        id="email"
                                                                        type="email"
                                                                        placeholder="Email address"
                                                                        className="mt-1.5"
                                                                        value={traveler.email}
                                                                        onChange={(e) => handleInputChange(index, 'email', e.target.value)}
                                                                    />
                                                                </div>
                                                                {/* <div>
                                                            <Label htmlFor="cemail" className="text-xs font-bold text-gray-500 uppercase">Confirm email address</Label>
                                                            <Input
                                                                id="cemail"
                                                                type="email"
                                                                placeholder="Confirm email address"
                                                                className="mt-1.5"
                                                                value={traveler.confirmEmail}
                                                                onChange={(e) => handleInputChange(index, 'confirmEmail', e.target.value)}
                                                            />
                                                        </div> */}
                                                                <div>
                                                                    <Label className="text-xs font-bold text-gray-500 uppercase">Contact Number (Required)</Label>
                                                                    <Input className="mt-1.5"
                                                                        placeholder="Mobile number"
                                                                        value={traveler.mobileNumber}
                                                                        onChange={(e) => handleInputChange(index, 'mobileNumber', e.target.value)}
                                                                    />
                                                                    <p className="text-xs text-muted-foreground mt-1">This is how we will get in touch with you.</p>
                                                                </div>
                                                            </div>

                                                            {/* <div>
                                                        <Label className="text-xs font-bold text-gray-500 uppercase">Contact Number (Required)</Label>
                                                        <div className="grid grid-cols-[120px_1fr] gap-4 mt-1.5">
                                                            <Select
                                                                value={traveler.countryCode}
                                                                onValueChange={(val) => handleInputChange(index, 'countryCode', val)}
                                                            >
                                                                <SelectTrigger><SelectValue placeholder="Country" /></SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="np">Nepal (+977)</SelectItem>
                                                                    <SelectItem value="us">USA (+1)</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                            <Input
                                                                placeholder="Mobile number"
                                                                value={traveler.mobileNumber}
                                                                onChange={(e) => handleInputChange(index, 'mobileNumber', e.target.value)}
                                                            />
                                                        </div>
                                                        <p className="text-xs text-muted-foreground mt-1">This is how we will get in touch with you.</p>
                                                    </div> */}
                                                        </>
                                                    )}

                                                    {/* DOB & Nationality */}
                                                    <div className="grid md:grid-cols-2 gap-4">
                                                        {/* <div className="grid grid-cols-3 gap-2 mt-1.5">
                                                        <Select><SelectTrigger><SelectValue placeholder="DD" /></SelectTrigger><SelectContent><SelectItem value="1">1</SelectItem></SelectContent></Select>
                                                        <Select><SelectTrigger><SelectValue placeholder="MM" /></SelectTrigger><SelectContent><SelectItem value="1">Jan</SelectItem></SelectContent></Select>
                                                        <Select><SelectTrigger><SelectValue placeholder="YYYY" /></SelectTrigger><SelectContent><SelectItem value="2000">2000</SelectItem></SelectContent></Select>
                                                    </div> */}
                                                        <div>
                                                            <Label className="text-xs font-bold text-gray-500 uppercase">Date of Birth (Required)</Label>
                                                            <DatePicker
                                                                value={traveler.dateOfBirth}
                                                                onChange={(date) => handleInputChange(index, 'dateOfBirth', date)}
                                                                placeholder="Select Date of Birth"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs font-bold text-gray-500 uppercase">Nationality</Label>
                                                            <Select
                                                                value={traveler.nationality}
                                                                onValueChange={(val) => handleInputChange(index, 'nationality', val)}
                                                            >
                                                                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select nationality" /></SelectTrigger>
                                                                <SelectContent>
                                                                    {searchFilters?.Countries?.length > 0 ? (
                                                                        searchFilters.Countries.map((c) => (
                                                                            <SelectItem key={c.CountryCode} value={c.CountryCode}>
                                                                                {c.Demonym || c.CountryName}
                                                                            </SelectItem>
                                                                        ))
                                                                    ) : (
                                                                        <div className="p-2 text-sm text-muted-foreground">Loading...</div>
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>

                                                    {/* Passport */}
                                                    {/* <div className="space-y-4 border-t pt-4">
                                                <div className="text-sm font-semibold">Passport (Required)</div>
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    <div>
                                                        <Input placeholder="Passport Number" />
                                                    </div>
                                                    <div>
                                                        <Select>
                                                            <SelectTrigger><SelectValue placeholder="Passport Issue Country" /></SelectTrigger>
                                                            <SelectContent><SelectItem value="np">Nepal</SelectItem></SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    <div>
                                                        <Label className="text-xs font-bold text-gray-500 uppercase">Issued Date</Label>
                                                        <div className="grid grid-cols-3 gap-2 mt-1.5">
                                                            <Select><SelectTrigger><SelectValue placeholder="DD" /></SelectTrigger><SelectContent><SelectItem value="01">01</SelectItem></SelectContent></Select>
                                                            <Select><SelectTrigger><SelectValue placeholder="MM" /></SelectTrigger><SelectContent><SelectItem value="01">Jan</SelectItem></SelectContent></Select>
                                                            <Select><SelectTrigger><SelectValue placeholder="YYYY" /></SelectTrigger><SelectContent><SelectItem value="2020">2020</SelectItem></SelectContent></Select>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs font-bold text-gray-500 uppercase">Expiration Date</Label>
                                                        <div className="grid grid-cols-3 gap-2 mt-1.5">
                                                            <Select><SelectTrigger><SelectValue placeholder="DD" /></SelectTrigger><SelectContent><SelectItem value="01">01</SelectItem></SelectContent></Select>
                                                            <Select><SelectTrigger><SelectValue placeholder="MM" /></SelectTrigger><SelectContent><SelectItem value="01">Jan</SelectItem></SelectContent></Select>
                                                            <Select><SelectTrigger><SelectValue placeholder="YYYY" /></SelectTrigger><SelectContent><SelectItem value="2030">2030</SelectItem></SelectContent></Select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> */}
                                                </>
                                            ) : (
                                                // for guest travellers
                                                <div className="space-y-6">
                                                    <div className="space-y-4">
                                                        <div className="text-sm font-semibold text-gray-700">Full Name (Required)</div>
                                                        <div className="grid md:grid-cols-6 gap-4">
                                                            <div className="md:col-span-1">
                                                                <Select
                                                                    value={traveler.title}
                                                                    onValueChange={(val) => handleInputChange(index, 'title', val)}
                                                                >
                                                                    <SelectTrigger id={`title-${index}`}><SelectValue placeholder="Title" /></SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="mr">Mr</SelectItem>
                                                                        <SelectItem value="mrs">Mrs</SelectItem>
                                                                        <SelectItem value="ms">Ms</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                            <div className="md:col-span-2">
                                                                <Input
                                                                    id={`fname-${index}`}
                                                                    placeholder="First Name"
                                                                    value={traveler.firstName}
                                                                    onChange={(e) => handleInputChange(index, 'firstName', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="md:col-span-3">
                                                                <Input
                                                                    id={`lname-${index}`}
                                                                    placeholder="Last Name"
                                                                    value={traveler.lastName}
                                                                    onChange={(e) => handleInputChange(index, 'lastName', e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-4">
                                                        <div className="text-sm font-semibold text-gray-700">Contact Details (Optional)</div>
                                                        <div className="grid md:grid-cols-2 gap-4">
                                                            <div>
                                                                <Label htmlFor={`email-${index}`} className="text-xs font-bold text-gray-500 uppercase">Email Address</Label>
                                                                <Input
                                                                    id={`email-${index}`}
                                                                    type="email"
                                                                    placeholder="Email address"
                                                                    className="mt-1.5"
                                                                    value={traveler.email}
                                                                    onChange={(e) => handleInputChange(index, 'email', e.target.value)}
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label className="text-xs font-bold text-gray-500 uppercase">Mobile Number</Label>
                                                                <div>
                                                                    {/* <Select
                                                                value={traveler.countryCode}
                                                                onValueChange={(val) => handleInputChange(index, 'countryCode', val)}
                                                            >
                                                                <SelectTrigger><SelectValue placeholder="Country" /></SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="np">Nepal (+977)</SelectItem>
                                                                    <SelectItem value="us">USA (+1)</SelectItem>
                                                                </SelectContent>
                                                            </Select> */}
                                                                    <Input className="mt-1.5"
                                                                        placeholder="Mobile number"
                                                                        value={traveler.mobileNumber}
                                                                        onChange={(e) => handleInputChange(index, 'mobileNumber', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="grid md:grid-cols-2 gap-4">
                                                        <div>
                                                            <Label className="text-xs font-bold text-gray-500 uppercase">Date of Birth (Required)</Label>
                                                            <DatePicker
                                                                value={traveler.dateOfBirth}
                                                                onChange={(date) => handleInputChange(index, 'dateOfBirth', date)}
                                                                placeholder="Select Date of Birth"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs font-bold text-gray-500 uppercase">Nationality (Required)</Label>
                                                            <Select
                                                                value={traveler.nationality}
                                                                onValueChange={(val) => handleInputChange(index, 'nationality', val)}
                                                            >
                                                                <SelectTrigger><SelectValue placeholder="Select nationality" /></SelectTrigger>
                                                                <SelectContent>
                                                                    {searchFilters?.Countries?.length > 0 ? (
                                                                        searchFilters.Countries.map((c) => (
                                                                            <SelectItem key={c.CountryCode} value={c.CountryCode}>
                                                                                {c.Demonym || c.CountryName}
                                                                            </SelectItem>
                                                                        ))
                                                                    ) : (
                                                                        <div className="p-2 text-sm text-muted-foreground">Loading...</div>
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">The traveller should be 12 years or above.</p>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            <Button
                                className="px-2 lg:px-4 w-full h-14 text-lg rounded-md"
                                onClick={handleReview}
                            >
                                Review Booking
                            </Button>
                        </>) : (
                        /* Review Section */
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <h2 className="text-xl font-bold">Review Your Booking</h2>
                                <p className="text-sm text-muted-foreground">Please review your details before confirming.</p>
                            </div>

                            <div className="space-y-4">
                                <Separator className="my-3" />

                                {travelers.map((traveler, index) => (
                                    <Card className="border shadow-sm">
                                        <CardHeader className="pb-4 bg-gray-50/50 border-b">
                                            <div className="flex items-center gap-2">
                                                <div className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                                                    {index + 1}
                                                </div>
                                                <span className="font-bold">Traveler {index + 1}</span>
                                                {index === 0 && <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded ml-2">Lead Traveler</span>}
                                            </div>
                                        </CardHeader>
                                        <CardContent className="p-6 space-y-4">

                                            <div key={traveler.id} className="grid grid-cols-2 gap-2 text-sm">
                                                <div className="font-semibold text-gray-700">
                                                    Name
                                                </div>
                                                <div>
                                                    {traveler.title && <span className="capitalize">{traveler.title}. </span>}
                                                    {traveler.firstName} {traveler.lastName}
                                                </div>

                                                {index === 0 ? (
                                                    <>
                                                        <div className="font-semibold text-gray-700">Email:</div>
                                                        <div>{traveler.email}</div>
                                                        <div className="font-semibold text-gray-700">Phone:</div>
                                                        <div>{traveler.mobileNumber}</div>
                                                        <div className="font-semibold text-gray-700">Date of Birth:</div>
                                                        <div>{traveler.dateOfBirth}</div>
                                                        <div className="font-semibold text-gray-700">Nationality:</div>
                                                        <div>{traveler.nationality}</div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="font-semibold text-gray-700">Date of Birth:</div>
                                                        <div>{traveler.dateOfBirth}</div>
                                                        <div className="font-semibold text-gray-700">Nationality:</div>
                                                        <div>{traveler.nationality}</div>
                                                    </>
                                                )}
                                            </div>

                                        </CardContent>
                                    </Card>
                                ))}

                                <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-4 rounded-md">
                                    <div className="flex gap-3">
                                        <Info className="w-5 h-5 shrink-0 mt-0.5" />
                                        <div className="text-sm">
                                            <p className="font-semibold mb-1">Confirmation Process</p>
                                            <p>By proceeding, you are confirming your booking request. Our team will review the details and contact you shortly to finalize the arrangements.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <Button
                                        variant="outline"
                                        className="flex-1 h-12 text-lg"
                                        onClick={() => setBookingStep('form')}
                                        disabled={isSubmitting}
                                    >
                                        Back to Edit
                                    </Button>
                                    <Button
                                        className="flex-[2] h-12 text-lg"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Processing..." : "Confirm Booking"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column - Sidebar */}
                <div className="space-y-6">
                    {/* Tour Summary Card */}
                    <Card className="shadow-sm">
                        <CardContent className="p-4">
                            <div className="flex gap-4 mb-4">
                                <div className="h-16 w-16 bg-gray-200 rounded-md shrink-0 overflow-hidden">
                                    {/* Placeholder image */}
                                    <img src={image || "https://placehold.co/100x100?text=Tour"} alt="Tour" className="h-full w-full object-cover" />
                                </div>
                                <div>
                                    <h3 className="font-bold line-clamp-2 text-sm">{String(packageTitle || "Everest Base Camp Trek")}</h3>
                                    {/* <div className="text-xs text-muted-foreground mt-1">Duration: 15 days</div> */}
                                    {/* <div className="text-yellow-500 text-xs mt-1 flex">★★★★★ <span className="text-gray-400 ml-1">5.0</span></div> */}
                                </div>
                            </div>
                            <Separator className="my-3" />
                            <div className="space-y-3 text-sm">
                                <div className="grid grid-cols-3 gap-2">
                                    <span className="text-muted-foreground font-medium">Starts in</span>
                                    <div className="col-span-2 text-right">
                                        {/* <div className="font-semibold">Kathmandu, Nepal</div> */}
                                        <div className="font-semibold">{String(startplace || "Kathmandu, Nepal")}</div>
                                        <div className="text-xs text-muted-foreground">{departureDate ? String(departureDate) : "06 February 2026"}</div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <span className="text-muted-foreground font-medium">Ends</span>
                                    <div className="col-span-2 text-right">
                                        <div className="font-semibold">{String(endplace || "Kathmandu, Nepal")}</div>
                                        <div className="text-xs text-muted-foreground">{returnDate ? String(returnDate) : "20 February 2026"}</div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <span className="text-muted-foreground font-medium">Tour Type</span>
                                    <div className="col-span-2 text-right font-semibold">{String(tourType) === "Group Tour" ? "Group Tour" : "Fixed Departure"}</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Price Breakdown */}
                    <Card className="shadow-sm">
                        <CardHeader className="pb-3 border-b">
                            <CardTitle className="text-base">Price Breakdown</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 space-y-3">
                            <div className="flex justify-between text-sm">
                                <div>
                                    <div className="font-medium">Brochure Price</div>
                                    <div className="text-xs text-muted-foreground">NPR {String(pricePerPerson || 1679)} x {travelerCount} traveler</div>
                                </div>
                                <div className="font-medium">NPR {((pricePerPerson || 1679) * travelerCount).toLocaleString()}</div>
                            </div>

                            {/* <div className="flex justify-between text-sm text-red-500">
                                <span className="font-medium">Special Trip Deal (30% Off)</span>
                                <span>- NPR {Math.round((pricePerPerson || 1679) * 0.3 * travelerCount)}</span>
                            </div> */}

                            <Separator />

                            <div className="flex justify-between items-center">
                                <div>
                                    <span className="font-bold text-lg block">Total Price</span>
                                    <span className="text-[10px] text-muted-foreground block">(Airfare Included)</span>
                                </div>
                                <span className="font-bold text-xl text-primary">NPR {Math.round((pricePerPerson || 1679) * travelerCount)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="font-medium">Deposit Payable Now</span>
                                <span className="font-bold">NPR {Math.round((pricePerPerson || 1679) * travelerCount)}</span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Info Card */}
                    {/* <div className="bg-green-50 border border-green-100 p-4 rounded-md flex gap-3">
                        <div className="h-10 w-1 bg-green-500 rounded-full shrink-0"></div>
                        <div>
                            <div className="font-bold text-sm">Travel Credits Reward <Info className="inline h-3 w-3 ml-1" /></div>
                            <div className="text-xs text-muted-foreground mt-1">Earn on tour completion • Use on future bookings</div>
                        </div>
                        <div className="ml-auto font-bold text-sm">USD 40</div>
                    </div> */}

                    {/* Support Card */}
                    <Card className="shadow-sm">
                        <CardContent className="p-4 space-y-3">
                            <div className="flex items-center gap-2 font-bold text-sm">
                                <span className="bg-green-100 text-green-700 p-1 rounded-full text-xs">?</span> Need Help?
                            </div>
                            <p className="text-xs text-muted-foreground">Questions about your booking? We're here to help.</p>
                            <Button variant="secondary" className="w-full text-sm h-8 bg-primary text-white hover:bg-primary/80 h-10" onClick={handleContactSupport}>Contact Support</Button>
                        </CardContent>
                    </Card>
                </div>
            </div >
        </div >
    );
}

function handleContactSupport() {
    window.location.href = Paths.CONTACT_US;
}
